import getProp from '@f/get-prop'
import { getSourceSettingCount } from './sources'
import { getWarehouseSettingsCount, isWorkspaceUsingWarehouseFilters } from './warehouses'
import { getMTUCountForPast30Days } from './mtus'
import { getWorkspaceOwnerCount, getWorkspaceFilteredEvents } from './workspaces'
import { isWorkspaceInvoicedAndWithQuota } from 'lib/utils/billing'
import { isMtuFreePlan, isMtuTeamPlan } from 'lib/utils/billing-plans'

export function getEstimatedMonthlyPayment (state, workspaceSlug) {
  const mtus = getMTUCountForPast30Days(state, workspaceSlug)
  const estimate = Math.ceil(mtus / 1000) * 10
  if (estimate < 100) {
    return 100
  }
  return estimate
}

export function hasBillingRequested (state, workspaceSlug) {
  const billing = getBilling(state, workspaceSlug)
  const { didRequestFail } = getBillingRequest(state, workspaceSlug)

  return Boolean(billing || didRequestFail)
}

export function getBillingRequest (state, workspaceSlug) {
  const request = getProp(`billingByWorkspaceSlug.${workspaceSlug}`, state) || {}
  return {
    hasRequested: request.hasRequested,
    isRequesting: request.isRequesting,
    didRequestFail: request.didRequestFail
  }
}

export function getBilling (state, workspaceSlug) {
  const billing = state.billingByWorkspaceSlug[workspaceSlug]
  return billing && billing.data
}

export function getBillingEmail (state, workspaceSlug) {
  const billing = getBilling(state, workspaceSlug) || {}
  return billing.email
}

export function getBillingSubscription (state, workspaceSlug) {
  const data = getBilling(state, workspaceSlug)
  if (!data) return null
  if (data.legacy) {
    return getProp('services.integrations.subscription', data)
  }
  return data.subscription
}

export function getBillingPlan (state, workspaceSlug) {
  const sub = getBillingSubscription(state, workspaceSlug)
  if (!sub) return null
  return sub.plan
}

export function isLegacyWorkspace (state, workspaceSlug) {
  const billing = getBilling(state, workspaceSlug) || {}
  return billing.legacy || false
}

export function getWarehouseBillingPlan (state, workspaceSlug) {
  const data = getBilling(state, workspaceSlug)
  if (!data) return null
  let subscription = null
  if (data.legacy) {
    subscription = getProp('services.warehouses.subscription', data)
  } else {
    subscription = data.subscription
  }
  if (!subscription) return null
  return subscription.plan
}

export function doesWorkspaceHaveCreditCardOnFile (state, workspaceSlug) {
  const billing = getBilling(state, workspaceSlug) || {}
  return !!billing.card
}

/**
 * Get checks for changing the current billing plan.
 */

export function getBillingPlanChangeChecks (state, workspaceSlug, nextPlan) {
  const currentPlan = getBillingPlan(state, workspaceSlug)
  const sourceCount = getSourceSettingCount(state, workspaceSlug)
  const seatCount = getWorkspaceOwnerCount(state, workspaceSlug)
  const warehouseCount = getWarehouseSettingsCount(state, workspaceSlug)
  const mtus = getMTUCountForPast30Days(state, workspaceSlug)
  const filteredTrackEvents = getWorkspaceFilteredEvents(state, workspaceSlug)

  const isUsingWarehouseFiltering = isWorkspaceUsingWarehouseFilters(state, workspaceSlug)
  const isUsingSourceFiltering = filteredTrackEvents.length > 0
  const isUsingFiltering = isUsingWarehouseFiltering || isUsingSourceFiltering

  // insufficient data
  if (!currentPlan) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('unknown billing plan for %s', workspaceSlug)
    }
    return null
  }

  const currentPlanId = currentPlan.id

  const result = {
    isDowngrading: false,
    isAvailable: true,
    canChangePlan: true,
    checks: []
  }

  if (isTeamPlan(currentPlanId) && (nextPlan.id && isMtuFreePlan(nextPlan.id))) {
    result.isDowngrading = true
  }

  if (nextPlan.id && isMtuFreePlan(nextPlan.id)) {
    result.checks = getDeveloperPlanChecks({
      mtus,
      sourceCount,
      seatCount,
      warehouseCount,
      isDowngrading: result.isDowngrading,
      nextPlanName: nextPlan.name
    })
  } else if (nextPlan.id && isMtuTeamPlan(nextPlan.id)) {
    // only possible when migrating a legacy team plan to a new team plan
    result.checks = getTeamPlanChecks({ seatCount, warehouseCount, nextPlanName: nextPlan.name })
  }

  if (isUsingFiltering) {
    result.checks.push({
      error: false,
      title: 'Schema and Event Filtering',
      messages: [
        'Current schema and event filter settings will migrate over',
        'Future modification of filters will be restricted to turning ON'
      ]
    })
  }

  result.canChangePlan = !result.checks.filter(c => c.error).length

  // Migrating isn't available if a check is enforced
  result.isAvailable = !result.checks.find(check => check.error === 'enforce')

  return result
}

/**
 * Checks whether a given workspace is a (paying) invoiced customer
 */

export function isInvoicedWorkspaceWithQuota (state, workspaceSlug) {
  const billing = getBilling(state, workspaceSlug)
  return isWorkspaceInvoicedAndWithQuota(billing)
}

function getDeveloperPlanChecks ({ mtus, sourceCount, seatCount, warehouseCount, isDowngrading, nextPlanName }) {
  const checks = []
  const errorMessageSuffix = getErrorMessageSuffix(isDowngrading, nextPlanName)

  if (sourceCount > 1) {
    checks.push({
      error: 'limit',
      title: '1 Source Limit',
      messages: [`Remove ${sourceCount - 1} sources ${errorMessageSuffix}`]
    })
  } else {
    checks.push({
      error: false,
      title: '1 Source'
    })
  }

  if (seatCount > 1) {
    checks.push({
      error: 'limit',
      title: '1 Seat Limit',
      messages: [`Remove ${seatCount - 1} team members ${errorMessageSuffix}`]
    })
  } else {
    checks.push({
      error: false,
      title: '1 Seat'
    })
  }

  if (warehouseCount > 1) {
    checks.push({
      error: 'limit',
      title: '1 Warehouse Limit',
      messages: [`Remove ${warehouseCount - 1} warehouses ${errorMessageSuffix}`]
    })
  } else if (warehouseCount === 1) {
    checks.push({
      error: false,
      title: '1 Warehouse Sync a day',
      messages: ['Your warehouse will sync once a day']
    })
  }

  if (mtus > 1000) {
    checks.push({
      error: 'enforce',
      title: '1,000 MTU limit',
      messages: ['Reduce your MTUs'] // TODO(stephenmathieson): get actual copy here
    })
  } else {
    checks.push({
      error: false,
      title: '1,000 MTU limit'
    })
  }

  return checks
}

function getTeamPlanChecks ({ seatCount, warehouseCount, nextPlanName }) {
  const checks = []
  const isDowngrading = false
  const errorMessageSuffix = getErrorMessageSuffix(isDowngrading, nextPlanName)

  if (seatCount > 7) {
    checks.push({
      error: 'limit',
      title: '1 Seat Limit',
      messages: [`Remove ${seatCount - 7} team members ${errorMessageSuffix}`]
    })
  } else {
    checks.push({
      error: false,
      title: '7 Seats limit',
      messages: [
        'Current team members will be migrated over',
        'Adding additional team members while over the limit will be restricted'
      ]
    })
  }

  if (warehouseCount > 1) {
    checks.push({
      error: 'limit',
      title: '1 Warehouse Limit',
      messages: [`Remove ${warehouseCount - 1} warehouses ${errorMessageSuffix}`]
    })
  } else if (warehouseCount === 1) {
    checks.push({
      error: false,
      title: '2 Warehouse Syncs a day',
      messages: ['Your warehouse will sync twice a day']
    })
  }

  return checks
}

function isTeamPlan (id) {
  switch (id) {
    case 'team-$100-1-month':
    case 'integrations-growth-$449-1-month': // legacy
      return true
    default:
      return false
  }
}

function getErrorMessageSuffix (isDowngrading, nextPlanName) {
  return isDowngrading ? 'to downgrade' : `to switch over to the ${nextPlanName} plan`
}



// WEBPACK FOOTER //
// ./selectors/billing.js